import React, { useId, useState } from 'react';
import { Box, Select, MenuItem, TablePagination, Tabs, Tab, Typography, ButtonBase, IconButton, Dialog, DialogTitle, DialogContent, CircularProgress } from '@mui/material';
import { CheckRounded, KeyboardArrowDownRounded, Close, DescriptionOutlined, TableChartOutlined, ChevronLeft, ChevronRight, FirstPage, LastPage, FileDownloadOutlined } from '@mui/icons-material';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import './PharmacyUI.css';

export const PharmacyTabs = ({ value, onChange, options, label = 'Secciones del formulario' }) => (
  <Tabs className="pharmacy-tabs" value={value} onChange={(_, next) => onChange(next)} variant="scrollable"
    scrollButtons="auto" allowScrollButtonsMobile aria-label={label}>
    {options.map((option, i) => {
      const item = typeof option === 'string' ? { label: option } : option;
      return <Tab key={item.value ?? i} value={item.value ?? i} disableRipple label={<span className="pharmacy-tab-label">{item.label}{item.badge ? <span className="pharmacy-tab-badge">{item.badge === true ? '•' : item.badge}</span> : null}</span>} />;
    })}
  </Tabs>
);

export const PharmacySelect = ({ label, value, onChange, disabled, children, w = 200, ...props }) => {
  const id = useId();
  const options = [];
  const collect = nodes => React.Children.forEach(nodes, child => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) collect(child.props.children);
    else options.push(child);
  });
  collect(children);
  return <Box className="pharmacy-field" sx={{ width:typeof w === 'object' ? w : {xs:'100%',sm:w}, maxWidth:'100%', minWidth:0 }}>
    {label && <label id={id+'-label'} htmlFor={id}>{label}</label>}
    <Select id={id} labelId={label ? id+'-label' : undefined} className="pharmacy-select" fullWidth
      value={String(value ?? '')} onChange={onChange} disabled={disabled} displayEmpty
      inputProps={{'aria-label':label || props['aria-label'] || 'Seleccionar opción'}}
      IconComponent={KeyboardArrowDownRounded}
      renderValue={selected => options.find(option => String(option.props.value ?? option.props.children ?? '') === selected)?.props.children ?? ''}
      MenuProps={{
        className:'pharmacy-select-menu', disableScrollLock:true, transitionDuration:0,
        anchorOrigin:{vertical:'bottom',horizontal:'left'}, transformOrigin:{vertical:'top',horizontal:'left'},
        PaperProps:{className:'pharmacy-select-paper'}, MenuListProps:{className:'pharmacy-select-list'},
      }}>
      {options.map((option,i) => <MenuItem disableRipple key={String(option.props.value ?? option.props.children ?? i)}
        value={String(option.props.value ?? option.props.children ?? '')} disabled={option.props.disabled} className="pharmacy-select-option">
        <span>{option.props.children}</span><CheckRounded className="pharmacy-select-check" aria-hidden="true"/>
      </MenuItem>)}
    </Select>
  </Box>;
};

export const PharmacyMetric = ({ label, value, valor, sub, color, accent, icon, Icon, progress, reference = false }) => (
  <Box className={'pharmacy-metric'+(reference ? ' pharmacy-metric-reference' : '')} sx={{'--metric-color':color || accent || '#007AFF'}}>
    <div className="pharmacy-metric-heading"><Typography className="pharmacy-metric-label">{label}</Typography>
      {(icon || Icon) && <span className="pharmacy-metric-icon">{icon || <Icon fontSize="small"/>}</span>}
    </div>
    <Typography className="pharmacy-metric-value">{value ?? valor ?? '—'}</Typography>
    {sub != null && <Typography className="pharmacy-metric-detail">{sub}</Typography>}
    {progress != null && <div className="pharmacy-progress"><span style={{width:Math.min(100,Math.max(0,Number(progress)||0))+'%'}}/></div>}
  </Box>
);

export const PharmacyChartCard = ({ title, sub, children, right, minH }) => (
  <Box className="pharmacy-chart-card"><div className="pharmacy-chart-heading"><div><Typography component="h2">{title}</Typography>{sub && <Typography className="pharmacy-chart-subtitle">{sub}</Typography>}</div>{right}</div><Box sx={{minHeight:minH,minWidth:0}}>{children}</Box></Box>
);

// Existing data, scales and callbacks remain owned by each report.
const chartOptions = (options = {}) => ({
  ...options, responsive:true, maintainAspectRatio:false,
  ...(window.matchMedia('(prefers-reduced-motion: reduce)').matches ? {animation:false} : {}),
  plugins:{...options.plugins, tooltip:{...options.plugins?.tooltip,backgroundColor:'#202C40',titleColor:'#FFFFFF',bodyColor:'#FFFFFF',padding:12,cornerRadius:12,titleFont:{size:12,weight:'600'},bodyFont:{size:12}},
    legend:{...options.plugins?.legend,labels:{...options.plugins?.legend?.labels,usePointStyle:true,boxWidth:8,boxHeight:8,padding:16,font:{size:12},color:'#667085'}}},
});
const makeChart = Component => ({options, ...props}) => <Component role="img" aria-label="Gráfico del informe" {...props} options={chartOptions(options)}/>;
export const PharmacyLine = makeChart(Line);
export const PharmacyBar = makeChart(Bar);
export const PharmacyDoughnut = makeChart(Doughnut);
export const PharmacyPie = makeChart(Pie);

export const PharmacyPagination = ({total=0,pagina=1,setPagina,rpp=20,setRpp}) => {
  const count=Math.max(0,Number(total)||0), size=Math.max(1,Number(rpp)||20);
  const pages=Math.max(1,Math.ceil(count/size));
  const current=Math.max(1,Math.min(pages,Number(pagina)||1));
  const sizes=[...new Set([10,20,25,50,100,200,size])].sort((a,b)=>a-b);
  return <Box component="nav" aria-label="Paginación de resultados" className="pharmacy-pagination">
    <div className="pharmacy-pagination-summary"><Typography role="status">{count ? (current-1)*size+1 : 0}–{Math.min(current*size,count)} de {count.toLocaleString('es-PE')}</Typography>
      {setRpp && <div className="pharmacy-page-size"><span>Filas</span><PharmacySelect aria-label="Filas por página" w={80} value={size} onChange={e=>{setRpp(Number(e.target.value));setPagina(1);}}>{sizes.map(n=><option key={n} value={n}>{n}</option>)}</PharmacySelect></div>}
    </div>
    <div className="pharmacy-pagination-controls">
      <IconButton aria-label="Primera página" disabled={current===1} onClick={()=>setPagina(1)}><FirstPage/></IconButton>
      <IconButton aria-label="Página anterior" disabled={current===1} onClick={()=>setPagina(current-1)}><ChevronLeft/></IconButton>
      <Typography>{current} / {pages}</Typography>
      <IconButton aria-label="Página siguiente" disabled={current===pages} onClick={()=>setPagina(current+1)}><ChevronRight/></IconButton>
      <IconButton aria-label="Última página" disabled={current===pages} onClick={()=>setPagina(pages)}><LastPage/></IconButton>
    </div>
  </Box>;
};

export const PharmacyDownloadDialog = ({open,onClose,title='Descargar reporte',subtitle,total,busy=false,options=[],children}) => {
  const id=useId(); const [selected,setSelected]=useState(null);
  return <Dialog open={Boolean(open)} onClose={()=>{if(!busy)onClose();}} aria-labelledby={id} className="pharmacy-dialog"
    sx={{zIndex:14000}} maxWidth="xs" fullWidth PaperProps={{className:'pharmacy-download-paper'}}>
    <DialogTitle id={id} component="div" className="pharmacy-dialog-heading">
      <span className="pharmacy-dialog-symbol"><FileDownloadOutlined/></span>
      <div><Typography component="h2">{title}</Typography><Typography className="pharmacy-dialog-subtitle">{subtitle || (total != null ? Number(total).toLocaleString('es-PE')+' registros · Elige un formato' : 'Elige cómo quieres guardar tu reporte')}</Typography></div>
      <IconButton aria-label="Cerrar descarga" disabled={Boolean(busy)} onClick={onClose}><Close/></IconButton>
    </DialogTitle>
    <DialogContent className="pharmacy-dialog-content">
      {children}
      <div className="pharmacy-download-options">{options.map((option,i)=>{
        const excel=/excel|xlsx/i.test(option.fmt || option.ext || option.label || '');
        return <ButtonBase key={option.fmt || i} className="pharmacy-download-option" disabled={Boolean(busy) || option.disabled} onClick={()=>{setSelected(i);option.onClick();}}>
          <span className={'pharmacy-format-icon '+(excel?'excel':'pdf')}>{excel ? <TableChartOutlined/> : <DescriptionOutlined/>}</span>
          <span className="pharmacy-download-copy"><strong>{option.label || (excel?'Descargar Excel':'Descargar PDF')}</strong><span>{option.sub || (excel?'Hoja de cálculo editable · .xlsx':'Documento para compartir · .pdf')}</span></span>
          {busy && selected===i ? <CircularProgress size={20}/> : <span className="pharmacy-format-extension">{option.ext || (excel?'XLSX':'PDF')}</span>}
        </ButtonBase>;
      })}</div>
      <Typography className="pharmacy-download-note" role="status">{busy ? 'Preparando el archivo…' : 'Se conservan los filtros y las opciones de este reporte.'}</Typography>
    </DialogContent>
  </Dialog>;
};

export const PharmacyTablePagination = props => <TablePagination {...props} SelectProps={{
  ...props.SelectProps, className:'pharmacy-pagination-picker', IconComponent:KeyboardArrowDownRounded,
  MenuProps:{className:'pharmacy-select-menu',disableScrollLock:true,transitionDuration:0,
    anchorOrigin:{vertical:'bottom',horizontal:'left'},transformOrigin:{vertical:'top',horizontal:'left'},
    PaperProps:{className:'pharmacy-select-paper'},MenuListProps:{className:'pharmacy-select-list'}},
}} />;
